import { FC, memo } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useBillingRepositoryInjector, useStreamRepositoryInjector } from 'application/providers/DIContainerProvider';
import AnalyticsService, { ParamNameAMP } from 'application/services/AnalyticsService';
import {
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
  ThematicEventNameAMP,
} from 'application/services/AnalyticsService/ampTypes';
import { NY_BANNER_TYPES_ENUM } from 'application/services/AnalyticsService/eventTypes/thematic';
import CompanyUtils from 'application/utils/CompanyUtils';
import { useOnboardingContext } from 'application/components/common';
import { useAccountCompanyData } from 'application/hooks';

import usePointList from 'domain/point/usePointList';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';

import { TEXTS } from './consts';

import styles from './index.module.scss';

const getFromTo = (playFromDate: string, playToDate: string) => {
  const parsedFrom = DateUtils.parse(playFromDate, 'yyyy-MM-dd');
  const parsedTo = DateUtils.parse(playToDate, 'yyyy-MM-dd');
  const from = DateUtils.getFormatDateFrom(parsedFrom, 'dd MMMM yyyy');
  const to = DateUtils.getFormatDateFrom(parsedTo, 'dd MMMM yyyy');

  return { from, to };
};

const AddNewYearContentModal: FC<ModalProps[ModalTypes.ADD_NEW_YEAR_CONTENT_MODAL]> = ({
  thematicBlock,
  close,
  replaceModal,
}) => {
  const { currentPoint } = useOnboardingContext();
  const currentCompany = useAccountCompanyData();

  const { watchBillingInfo } = useBillingRepositoryInjector();
  const { setStreamWithThematic } = useStreamRepositoryInjector();

  const { canRealizeBillingOperation } = watchBillingInfo(currentCompany.companyData?.id!);

  const { from, to } = getFromTo(thematicBlock.playFromDate, thematicBlock.playToDate);

  const [_, result] = usePointList();

  const onOpenNextModal = async () => {
    const isDenyCreatePoint = CompanyUtils.isDenyCreatePoint(canRealizeBillingOperation, currentCompany.companyData);

    let modalType;

    const actualPoints = result.data?.points;

    if (isDenyCreatePoint && actualPoints && actualPoints.length === 0) {
      modalType = ModalTypes.DENY_OPERATION;
      replaceModal(ModalTypes.DENY_OPERATION, {});
    } else if (!!currentPoint || (actualPoints && actualPoints.length !== 0)) {
      modalType = ModalTypes.STREAM_BIND;
      replaceModal(ModalTypes.STREAM_BIND, {});
    } else {
      replaceModal(ModalTypes.POINT_CREATE, {});
    }

    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_RUN_CLICKED, {
      [StreamBindFlowParamNameAMP.STREAM_BIND_MODAL_TYPE]: modalType,
    });
  };

  const handleSkip = () => {
    AnalyticsService.event(ThematicEventNameAMP.SKIP_THEMATIC_CONTENT_PRESSED, {
      [ParamNameAMP.TYPE]: NY_BANNER_TYPES_ENUM.MODAL_ONE_WAVE,
    });

    onOpenNextModal();
  };

  const handleAdd = () => {
    AnalyticsService.event(ThematicEventNameAMP.ADD_THEMATIC_CONTENT_PRESSED, {
      [ParamNameAMP.TYPE]: NY_BANNER_TYPES_ENUM.MODAL_ONE_WAVE,
    });

    setStreamWithThematic(true);
    onOpenNextModal();
  };

  return (
    <ModalScreen
      onClose={close}
      actionButtons={[
        <Button
          elementId="new_year_run_stream_add"
          type={ButtonType.PRIMARY}
          onClick={handleAdd}>
          {TEXTS.ADD_BUTTON}
        </Button>,
        <Button
          elementId="new_year_run_stream_skip"
          type={ButtonType.SECONDARY}
          onClick={handleSkip}>
          {TEXTS.SKIP_BUTTON}
        </Button>,
      ]}>
      <div className={styles.AddNewYearContentModal}>
        <div className={styles.AddNewYearContentModalCover} />
        <h2>{TEXTS.TITLE}</h2>
        <p>
          {TEXTS.DESCRIPTION_PART_ONE}
          <strong>
            {from}
            {TEXTS.DESCRIPTION_PART_TWO}
            {to}
          </strong>
          {TEXTS.DESCRIPTION_PART_THREE} {TEXTS.DESCRIPTION_PART_FOUR} <strong>{TEXTS.DESCRIPTION_PART_FIVE}</strong>
        </p>
        <p>{TEXTS.SUB_DESCRIPTION_TEXT}</p>
      </div>
    </ModalScreen>
  );
};

export default memo(AddNewYearContentModal);
